import { api } from './axios';

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с голосованием в раундах баттлов
 *
 * Поддерживаемые системы голосования:
 * - PLUS_MINUS_ZERO: +/0/- (score: -1, 0, 1)
 * - NUMERIC_10: 0-10
 * - CRITERIA_BASED: по критериям
 * - CUSTOM_XYZ: кастомные значения (X/Y/Z)
 *
 * Голосовые комментарии:
 * - Загрузите файл через /api/files/upload (type=AUDIO)
 * - Передайте voiceCommentId в payload
 */
export const votesApi = {
    /**
     * Проголосовать за трек в раунде
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @param {Object} payload - { trackId, score, comment?, voiceCommentId?, criteriaScores? }
     * @param {string} payload.trackId - UUID трека
     * @param {number} payload.score - Оценка (зависит от системы голосования)
     * @param {string} [payload.comment] - Текстовый комментарий
     * @param {string} [payload.voiceCommentId] - UUID голосового комментария (файл)
     * @param {Array<{criterionName: string, score: number}>} [payload.criteriaScores] - Оценки по критериям (для CRITERIA_BASED)
     * @returns {Promise<VoteResponse>}
     */
    vote: async (battleId, roundId, payload) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Vote payload is required');
        try {
            const { data } = await api.post(`/battles/${battleId}/rounds/${roundId}/vote`, payload);
            return data;
        } catch (error) {
            console.error(`[votesApi.vote] Failed to vote in round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить свой голос
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @param {Object} payload - { trackId, score, comment?, voiceCommentId?, criteriaScores? }
     * @param {string} payload.trackId - UUID трека
     * @param {number} payload.score - Оценка
     * @param {string} [payload.comment] - Текстовый комментарий
     * @param {string} [payload.voiceCommentId] - UUID голосового комментария
     * @param {Array<{criterionName: string, score: number}>} [payload.criteriaScores] - Оценки по критериям
     * @returns {Promise<VoteResponse>}
     */
    updateVote: async (battleId, roundId, payload) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Vote payload is required');
        try {
            const { data } = await api.put(`/battles/${battleId}/rounds/${roundId}/vote`, payload);
            return data;
        } catch (error) {
            console.error(`[votesApi.updateVote] Failed to update vote in round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить голос
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @param {string|number} trackId - ID трека
     * @returns {Promise<VoteResponse>}
     */
    deleteVote: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            const { data } = await api.delete(`/battles/${battleId}/rounds/${roundId}/vote/${trackId}`);
            return data;
        } catch (error) {
            console.error(`[votesApi.deleteVote] Failed to delete vote in round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить результаты голосования раунда
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<{averageScore: number|null, totalScore: number|null, votesCount: number, votes: VoteResponse[]}>}
     */
    getResults: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rounds/${roundId}/results`);
            return data;
        } catch (error) {
            console.error(`[votesApi.getResults] Failed to fetch results for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить список голосов раунда (ADMIN/MODERATOR)
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<Array>}
     */
    getVotes: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rounds/${roundId}/votes`);
            return data;
        } catch (error) {
            console.error(`[votesApi.getVotes] Failed to fetch votes for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Открыть сессию голосования (ADMIN)
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<Object>}
     */
    openSession: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.post(`/battles/${battleId}/rounds/${roundId}/session/open`);
            return data;
        } catch (error) {
            console.error(`[votesApi.openSession] Failed to open session for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Закрыть сессию голосования (ADMIN)
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<Object>}
     */
    closeSession: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.post(`/battles/${battleId}/rounds/${roundId}/session/close`);
            return data;
        } catch (error) {
            console.error(`[votesApi.closeSession] Failed to close session for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить текущую сессию голосования
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<Object>}
     */
    getSession: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rounds/${roundId}/session`);
            return data;
        } catch (error) {
            console.error(`[votesApi.getSession] Failed to fetch session for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить, проголосовал ли текущий пользователь за трек
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @param {string|number} trackId - ID трека
     * @returns {Promise<Object>} { hasVoted: boolean, votedForTrackId?: string }
     */
    hasVoted: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rounds/${roundId}/voted`, { params: { trackId } });
            return data;
        } catch (error) {
            console.error(`[votesApi.hasVoted] Failed to check vote status for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить правило голосования для раунда
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @returns {Promise<Object>} VotingRuleResponse
     */
    getRule: async (battleId, roundId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rounds/${roundId}/rule`);
            return data;
        } catch (error) {
            console.error(`[votesApi.getRule] Failed to fetch rule for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить правило голосования для баттла (общее правило)
     * @param {string|number} battleId - ID баттла
     * @returns {Promise<Object>} VotingRuleResponse
     */
    getBattleRule: async (battleId) => {
        validateId(battleId, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/rule`);
            return data;
        } catch (error) {
            console.error(`[votesApi.getBattleRule] Failed to fetch battle rule:`, error.message);
            throw error;
        }
    },

    /**
     * Создать правило голосования для раунда
     * @param {string|number} battleId - ID баттла
     * @param {string|number} roundId - ID раунда
     * @param {Object} payload - VotingRuleRequest
     * @param {VotingSystem} payload.votingSystem - Система голосования
     * @param {number[]} [payload.customValues] - Кастомные значения для CUSTOM_XYZ
     * @param {number} [payload.maxScore] - Максимальный балл
     * @param {string[]} [payload.judgingCriteria] - Критерии оценки
     * @param {boolean} [payload.isPublicVoting] - Публичное голосование
     * @param {number} [payload.minVotesToWin] - Минимум голосов для победы
     * @param {boolean} [payload.allowVoiceComment] - Разрешить голосовые комментарии
     * @param {boolean} [payload.requireComment] - Требовать обязательный комментарий
     * @returns {Promise<VotingRuleResponse>}
     */
    createRule: async (battleId, roundId, payload) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.post(`/battles/${battleId}/rounds/${roundId}/rule`, payload);
            return data;
        } catch (error) {
            console.error(`[votesApi.createRule] Failed to create rule for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Загрузить голосовой комментарий (аудиофайл)
     * @param {File} file - Аудиофайл комментария
     * @param {Function} [onProgress] - Callback для отслеживания прогресса (0-100)
     * @returns {Promise<{id: string, url: string, filename: string, size: number, mimeType: string}>}
     */
    uploadVoiceComment: async (file, onProgress) => {
        if (!file) throw new Error('File is required');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'AUDIO');

        try {
            const { data } = await api.post('/files/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(percentCompleted);
                    }
                }
            });
            return data;
        } catch (error) {
            if (error.response?.status === 413) {
                throw new Error('Размер файла превышает допустимый лимит (макс. 30 МБ)');
            }
            if (error.response?.status === 415) {
                throw new Error('Неподдерживаемый формат файла. Допустимы: MP3, WAV, OGG, FLAC, AAC, M4A');
            }
            console.error('[votesApi.uploadVoiceComment] Failed to upload voice comment:', error.message);
            throw error;
        }
    }
};
